import { useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Switcher from '@sdk/components/forms/Switcher';
import Href from '@sdk/components/ui/Href';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Spinner from '@sdk/components/ui/Spinner';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext from '@sdk/lang/gettext';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import type { PremiumGuardPassedConfig } from '../../premiumGuard/PremiumGuard';
import type { GoogleCalendarUtilsType } from '../types';
import PremiumGuardLabel from '../../premiumGuard/PremiumGuardLabel';
import { GOOGLE_CALENDAR_SYNC_URL } from '../constants';
import { useUserSettings } from '../UserSettingsContext';

import SettingsSection from './SettingsSection';
import RowSpaceBetween from './RowSpaceBetween';

const StyledRow = styled(Row)`
  align-items: center;
`;

type Props = {
  googleCalendarUtils: GoogleCalendarUtilsType;
  premiumGuard: PremiumGuardPassedConfig;
};

const GoogleCalendarSync: React.FC<React.PropsWithChildren<Props>> = ({
  googleCalendarUtils,
  premiumGuard,
}) => {
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const [googleAuthenticationInProgress, setGoogleAuthenticationInProgress] = useState(false);
  const [googleAuthenticated, setGoogleAuthenticated] = useState(false);

  const enableCalendarSync = async (): Promise<void> => {
    try {
      const code = await googleCalendarUtils.openGoogleCalendarModal();
      await googleCalendarUtils.authenticateGoogleCalendar(code);
      onUserSettingsChange({ googleCalendarSyncEnabled: true });
      setGoogleAuthenticated(true);
      setGoogleAuthenticationInProgress(false);
    } catch (error) {
      onUserSettingsChange({ googleCalendarSyncEnabled: false });
      setGoogleAuthenticationInProgress(false);
    }
  };

  const setGoogleCalendarSync = (value: boolean): void => {
    if (!value || googleAuthenticated) {
      onUserSettingsChange({ googleCalendarSyncEnabled: value });
      return;
    }
    setGoogleAuthenticationInProgress(true);
    enableCalendarSync();
  };

  return (
    <>
      <SettingsSection>
        <RowSpaceBetween>
          <Column>
            <StyledRow>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {gettext('Google calendar synchronization')}
              </Text14>
              {!premiumGuard.hasAccess && (
                <PremiumGuardLabel
                  tooltipId={premiumGuard.tooltipId}
                  label={premiumGuard.requiredPlan}
                  tooltipText={premiumGuard.tooltipContent}
                  pLeft={10}
                />
              )}
            </StyledRow>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext('Subscribe to your personal schedule on Google calendar')}
            </Text14>
            <Href
              underline
              themeColor={ThemeColorType.gray}
              fontSize={14}
              target="_blank"
              href={GOOGLE_CALENDAR_SYNC_URL}>
              {gettext('Learn More')}
            </Href>
          </Column>
          <Row pLeft={80}>
            {googleAuthenticationInProgress && (
              <StyledRow>
                <Spinner />
              </StyledRow>
            )}
            <Switcher
              value={userSettings.googleCalendarSyncEnabled}
              onChange={(value): void => setGoogleCalendarSync(value)}
              disabled={premiumGuard.hasAccess && googleAuthenticationInProgress}
              small
            />
          </Row>
        </RowSpaceBetween>
      </SettingsSection>
      <Row mTop={20} mBottom={20}>
        <HorizontalSeparator />
      </Row>
    </>
  );
};

export default GoogleCalendarSync;
